import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useRegTheme } from "../theme";
import { canRegister } from "../permissions";
import { submitRegisterOrder } from "../data/submitOrder";
import { generateOrderId, generateUuidV4 } from "../engine/savePayload";
import { getLoyaltyConfig, customerStoreCredit, customerLoyaltyBalance, lookupGiftCard } from "../data/tenderData";
import {
  applyGiftCard,
  removeGiftCard,
  applyLoyalty,
  clearLoyalty,
  applyStoreCredit,
  clearStoreCredit,
  addPayment,
  removePayment,
  setActiveOrderId,
  resetCart,
  setNcaRemoved,
} from "../../../Redux/features/WebRegister/cartSlice";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import { fmt } from "../uiUtils";
import TerminalPaymentModal from "./TerminalPaymentModal";
import { saveActiveOrder, clearActiveOrder } from "../data/activeOrder";
import { validLoyalty, validStoreCredit } from "../validators";
import { quickCashList } from "../data/registerSettings";
import { ncaFor, cashbackFeeFor, cashbackEnabled, getEnableOrderNumber, isDebitCardDisabled, ebtFoodEnabled, ebtCashEnabled, giftCardTenderEnabled } from "../data/registerSettings";
import { buildReceiptModel } from "../data/receipt";
import { kickDrawer } from "../data/cashDrawer";
import { roundHalfUp, isDualPrice, dualCardCharge, toBasePrice, splitCardBaseRatio } from "../engine";
import { nodeRefundTerminalPayment } from "../data/api";
import NumericInput from "../NumericInput";

// Multi-tender payment screen (port of AmountToPayFragment). The Balance box is tappable
// ("Tap to adjust and make a partial payment") → opens a numpad that sets `payAmount`, the amount
// the NEXT tender charges. This is how a partial/split payment is made (e.g. $5 of a $10 balance by
// card). Cash/Credit/Debit/EBT pay down the balance (each a split row when 2+); Gift Card/Loyalty/
// Store Credit are order-level reductions. The order finalizes the moment the balance reaches $0.
const CARD_TENDERS = { credit: "Credit", debit: "Debit", ebtcash: "CashEbt", ebtfood: "FoodEbt" };

export default function PaymentMethodModal({ onClose, resumeOrderId, settings, onComplete, deziAwait, deziWaitWarranted, initialPayAmount = 0 }) {
  const t = useRegTheme();
  const dispatch = useDispatch();
  const store = useStore();
  const cart = useSelector((st) => st.webRegisterCart);
  const balanceDue = cart.totals.balanceDue;

  // ONE order id per sale, owned by the CART (not this modal): reuse a resumed order's id, else the
  // id from a previous open of this screen (close/reopen mid-split), else generate. Prevents a
  // finished order from ever resurfacing as a phantom "unfinished" one.
  const orderIdRef = useRef(resumeOrderId || store.getState().webRegisterCart.activeOrderId || generateOrderId());
  useEffect(() => {
    dispatch(setActiveOrderId(orderIdRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Directly-typable "amount the next tender charges" (digit-only 0.00 field — no numpad modal;
  // desktop keyboard, or the OS numeric keyboard on touch). Defaults to the full balance and resets
  // to it after each tender. Type a smaller amount to pay part of the balance (split).
  // Android NUMPAD_PAYOUT (quickPayAmount): an amount keyed on the register numpad before Pay
  // pre-fills "Amount to pay" for the FIRST tender; after any tender it tracks the balance again.
  const [payAmount, setPayAmount] = useState(Number(initialPayAmount) > 0 ? Number(initialPayAmount) : balanceDue);
  const firstTenderRef = useRef(true);
  useEffect(() => {
    if (firstTenderRef.current && Number(initialPayAmount) > 0) {
      firstTenderRef.current = false;
      return;
    }
    firstTenderRef.current = false;
    setPayAmount(balanceDue);
  }, [balanceDue, initialPayAmount]);
  const pay = Math.min(payAmount, balanceDue);

  const [view, setView] = useState("select"); // select | giftcard
  const [terminal, setTerminal] = useState(null); // { cardType, amount, baseAmount, nca…, cashback…, ids }
  const [cashbackPrompt, setCashbackPrompt] = useState(null); // { key, value } — debit cash-back entry
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState("");

  // gift card sub-state
  const [gcNumber, setGcNumber] = useState("");
  const [gcLookup, setGcLookup] = useState(null); // { balance } | { error }
  const [gcAmount, setGcAmount] = useState(0);
  const [gcBusy, setGcBusy] = useState(false);

  // loyalty config + customer balances
  const [loyaltyCfg, setLoyaltyCfg] = useState(null);
  useEffect(() => {
    getLoyaltyConfig().then(setLoyaltyCfg).catch(() => setLoyaltyCfg(null));
    // warm the enable_order_number cache so the LOCAL-FIRST finalize builds its payload instantly
    getEnableOrderNumber().catch(() => {});
  }, []);
  const scBalance = customerStoreCredit(cart.customer);
  const loyaltyBal = customerLoyaltyBalance(cart.customer, loyaltyCfg && loyaltyCfg.dollarPerPoint);
  const loyaltyAvailable = !!(loyaltyCfg && loyaltyCfg.enabled && cart.customer && loyaltyBal.known && loyaltyBal.dollars > 0);
  const scAvailable = !!(cart.customer && scBalance > 0);

  // Finalize when the balance is cleared. `cashPaid` feeds change_due for a final cash tender.
  const finalize = async (cashPaid = 0) => {
    setBusy(true);
    setSaveError("");
    try {
      const res = await submitRegisterOrder({ cart: store.getState().webRegisterCart, orderId: orderIdRef.current, cashPaid });
      if (res && (res.status === true || res.status === "true" || res.queued)) {
        // Build the receipt from the finalized cart BEFORE resetting, then hand it to the receipt step.
        const model = buildReceiptModel({
          cart: store.getState().webRegisterCart,
          orderId: orderIdRef.current,
          cashPaid,
          isSurcharge: settings && settings.isSurcharge,
          payload: res.payload, // dual price: the receipt renders what was SAVED (Android)
        });
        // Local-first: the order is safely persisted (queue or server) — the receipt never waits.
        ToastifyAlert("Order saved", "success");
        dispatch(resetCart());
        onClose();
        if (onComplete) onComplete(model);
      } else {
        setSaveError((res && res.message) || "Order save failed. Please retry.");
      }
    } catch (e) {
      setSaveError("Order save failed: " + (e && e.message ? e.message : e));
    } finally {
      setBusy(false);
    }
  };

  const afterTenderAdded = (cashPaid = 0) => {
    const st = store.getState().webRegisterCart;
    saveActiveOrder(orderIdRef.current, st); // crash-safe: persist the in-progress order after each tender
    if (st.totals.balanceDue <= 0) finalize(cashPaid);
    else setView("select");
  };

  // Abandon-close: drop the in-progress record if nothing was charged; keep it (for crash recovery /
  // reconciliation) if a real card charge was already applied but the order wasn't finalized.
  const handleClose = () => {
    const st = store.getState().webRegisterCart;
    const hasCardCharge = (st.payments || []).some((p) => String(p.payType).toLowerCase() !== "cash" && Number(p.amount) > 0);
    if (!hasCardCharge) clearActiveOrder(orderIdRef.current);
    onClose();
  };

  /**
   * QV-7124 AWAIT-AT-TENDER — wait for an in-flight manufacturer-discount lookup, then re-run the
   * tender against the SETTLED total.
   *
   * WHY (money): this screen reads the totals live and the tender saves what it reads. Attaching a
   * customer HERE re-triggers the lookup (mKonnekt can add a loyalty-funded allowance on top of the
   * product one), so tapping a tender inside that window would charge and save the pre-discount
   * total. Android device-proved the difference: $105.22 charged vs $103.86 owed.
   *
   * It is a WAIT, not a freeze — the discount is applied and then charged, never suppressed. Bounded
   * by the hook's timeout, so a hung lookup cannot wedge the register.
   */
  const withDeziSettled = (fn) => async (...args) => {
    if (deziWaitWarranted && deziWaitWarranted() && deziAwait) {
      setBusy(true);
      try {
        await deziAwait();
      } finally {
        setBusy(false);
      }
      if (store.getState().webRegisterCart.lines.length === 0) return; // cart cleared while waiting
    }
    fn(...args);
  };

  // ── payment tenders (charge `pay`) ──
  const onCash = withDeziSettled(() => {
    if (!canRegister("MENU_CASH_PAYMENT")) return ToastifyAlert("Access denied", "error");
    // Android cashPayment: inputAmount ≥ mGrandTotal → the tender pays the balance and the excess is
    // change due (calculateExtraCash); a smaller amount is a partial (split) cash tender.
    const due = store.getState().webRegisterCart.totals.balanceDue;
    const tendered = Number(payAmount) || 0;
    const amt = Math.min(tendered, due);
    if (amt <= 0) return ToastifyAlert("Nothing to pay", "error");
    dispatch(addPayment({ payType: "cash", amount: amt }));
    kickDrawer(settings); // cash movement → open drawer (Android openOnlyCashDrawer)
    afterTenderAdded(tendered > due ? tendered : amt); // overpay shows as change_due
  });
  const onQuickCash = withDeziSettled((tendered) => {
    if (!canRegister("MENU_CASH_PAYMENT")) return ToastifyAlert("Access denied", "error");
    const due = store.getState().webRegisterCart.totals.balanceDue;
    dispatch(addPayment({ payType: "cash", amount: Math.min(tendered, due) }));
    kickDrawer(settings); // cash movement → open drawer
    afterTenderAdded(tendered); // overpay (tendered > balance) shows as change_due
  });
  // SNAP compliance: EBT Food can only pay for food-stampable line items. Cap = food-stampable line
  // nets minus any EBT-Food already tendered this order (Android remainingFoodStampableAmount).
  // Android remainingFoodStampableAmount (OCV:6983): min(toBase(totalFoodStampable) − Σ food-EBT legs,
  // cash owed). totalFoodStampable already carries each line's tax minus its order-discount share.
  const foodStampableRemaining = () => {
    const c = store.getState().webRegisterCart;
    const eligible = toBasePrice(c.dualPrice, Number(c.totals.foodStampable) || 0);
    const already = (c.payments || [])
      .filter((p) => String(p.payType) === "foodebt")
      .reduce((s2, p) => s2 + (Number(p.amount) || 0), 0);
    return Math.min(Math.max(roundHalfUp(eligible - already), 0), Math.max(Number(c.totals.balanceDue) || 0, 0));
  };
  // QV-4241 dual price — the CREDIT tile charges the card price (engine/dualPrice dualCardCharge).
  const dualOn = isDualPrice(cart.dualPrice) && !!cart.totals.dual;
  const creditQuote = () => {
    const c = store.getState().webRegisterCart;
    return dualCardCharge(c.dualPrice, {
      dual: c.totals.dual,
      balanceDue: c.totals.balanceDue,
      typed: Number(payAmount) || 0,
      isSplit: (c.payments || []).length > 0,
    });
  };
  // Charge a card tender: base amount pays the balance; NCA/surcharge + debit cashback are added ON
  // TOP (what the card is actually charged). The tender records base + fee breakdown for the payload.
  const doStartCard = (key, cashbackAmount = 0) => {
    const cardType = CARD_TENDERS[key];
    let base = pay;
    if (key === "ebtfood") {
      const cap = foodStampableRemaining();
      if (cap <= 0) return ToastifyAlert("No food-stampable items in the cart", "error");
      base = Math.min(pay, cap); // never charge EBT Food beyond the food-eligible amount
    }
    // NCA mode ("1") only; waived by the cashier's cbNca (Android isNcaWaived = ncaRemoved && !dual).
    let nca = ncaFor(dualOn || cartNcaRemoved() ? null : settings, base, cardType);
    if (dualOn && key === "credit") {
      // Dual price: the premium is INSIDE the card price — charge it, book base + (charge − base).
      const q = creditQuote();
      if (q.base <= 0) return ToastifyAlert("Nothing to pay", "error");
      base = q.base;
      nca = { amount: q.nca, percent: q.percent };
    }
    const cbFee = cashbackAmount > 0 ? cashbackFeeFor(settings, cardType) : 0;
    const charge = roundHalfUp(base + nca.amount + cashbackAmount + cbFee);
    const terminalOrderId = `${orderIdRef.current}-${generateOrderId().slice(0, 6)}`;
    setTerminal({
      cardType,
      amount: charge, // what the PIN pad charges (base + surcharge + cashback + fee)
      baseAmount: base, // what pays down the balance
      ncaAmount: nca.amount,
      ncaPercent: nca.percent,
      cashbackAmount,
      cashbackFee: cbFee,
      terminalOrderId,
      idempotencyKey: generateUuidV4(), // Payroc requires a UUID v4 Idempotency-Key
    });
  };
  const cartNcaRemoved = () => !!store.getState().webRegisterCart.ncaRemoved;
  // Android ATP:908-1046 — a typed amount above what that tender may take is REJECTED (never silently
  // clamped) and the box is restored to the full remaining amount.
  const overTender = (key) => {
    const c = store.getState().webRegisterCart;
    const typed = roundHalfUp(Number(payAmount) || 0);
    const due = roundHalfUp(c.totals.balanceDue);
    if (key === "credit") {
      // dual: the card-priced remainder (Android customerOwes in card dollars); else the cash remainder.
      const max = dualOn ? dualCardCharge(c.dualPrice, { dual: c.totals.dual, balanceDue: due, typed: 0, isSplit: (c.payments || []).length > 0 }).charge : due;
      return typed > roundHalfUp(max) ? "Entered amount must be less than or equal to the remaining amount." : "";
    }
    if (key === "ebtfood") {
      const cap = foodStampableRemaining();
      return typed > cap && typed !== due ? `Entered amount cannot be more than ${fmt(cap)} for this payment method.` : "";
    }
    return typed > due ? `Entered amount cannot be more than ${fmt(due)} for this payment method.` : "";
  };
  const restoreAmount = () => setPayAmount(store.getState().webRegisterCart.totals.balanceDue);
  const startCard = withDeziSettled((key) => {
    const perm = key === "credit" || key === "debit" ? "MENU_CREDIT_DEBIT_PAYMENT" : key === "ebtcash" ? "MENU_CASH_EBT" : "MENU_FOOD_EBT";
    if (!canRegister(perm)) return ToastifyAlert("Access denied", "error");
    if (key === "debit" && isDebitCardDisabled(settings))
      return ToastifyAlert("Debit card is disabled for this store", "error");
    if (pay <= 0) return ToastifyAlert("Nothing to pay", "error");
    const over = overTender(key);
    if (over) {
      restoreAmount();
      return ToastifyAlert(over, "error");
    }
    // Debit + cashback enabled → prompt for the cash-back amount first (Android custom-cashback dialog).
    if (key === "debit" && cashbackEnabled(settings, "Debit") && canRegister("MENU_CASH_BACK")) {
      setCashbackPrompt({ key, value: 0 });
      return;
    }
    doStartCard(key, 0);
  });
  const onCardApproved = (result) => {
    const label = terminal.cardType; // Credit/Debit/FoodEbt/CashEbt
    dispatch(
      addPayment({
        payType: label.toLowerCase().includes("ebt") ? (label === "FoodEbt" ? "foodebt" : "cashebt") : label.toLowerCase(),
        amount: terminal.baseAmount != null ? terminal.baseAmount : terminal.amount, // base pays balance
        ncaAmount: terminal.ncaAmount,
        ncaPercent: terminal.ncaPercent,
        cashbackAmount: terminal.cashbackAmount,
        cashbackFee: terminal.cashbackFee,
        tip: result.tipAmount,
        paymentId: result.paymentId,
        cardType: `${label}/${result.cardBrand || ""}`,
        last4: result.cardLast4,
        paxDetails: result.raw ? JSON.stringify(result.raw) : "",
        paymentResult: result.responseMessage,
        cvvResult: result.cvvResult,
      })
    );
    if (Number(terminal.cashbackAmount) > 0) kickDrawer(settings); // debit cash-back dispenses cash (Android openOnlyCashDrawer//cashback)
    setTerminal(null);
    afterTenderAdded(0);
  };

  // ── reductions ──
  const doGiftLookup = async () => {
    setGcBusy(true);
    setGcLookup(null);
    try {
      const r = await lookupGiftCard(gcNumber);
      if (r.found) {
        setGcLookup({ balance: r.balance });
        // dual price before a split: Android prefills/caps at the CARD total (customerOwes = mGrandTotal)
        const gcCap = dualOn && (cart.payments || []).length === 0 ? Number(cart.totals.grandTotal) || 0 : pay > 0 ? pay : balanceDue;
        setGcAmount(Math.min(r.balance, gcCap));
      } else {
        setGcLookup({ error: r.reason });
      }
    } finally {
      setGcBusy(false);
    }
  };
  const onApplyGift = () => {
    const amt = Math.max(Number(gcAmount) || 0, 0);
    if (amt <= 0) return ToastifyAlert("Enter an amount", "error");
    dispatch(applyGiftCard({ number: gcNumber.trim(), balance: gcLookup.balance, amount: amt }));
    setGcNumber("");
    setGcLookup(null);
    setGcAmount(0);
    afterTenderAdded(0);
  };
  const onLoyalty = () => {
    // Android validUsePoint guard chain (customer / split / gift-card / store-credit / already / OTD).
    const v = validLoyalty(store.getState().webRegisterCart, { hasCustomer: !!cart.customer, available: loyaltyBal.dollars });
    if (!v.ok) return ToastifyAlert(v.message, "error");
    const amt = Math.min(pay, loyaltyBal.dollars);
    if (amt <= 0) return ToastifyAlert("Nothing to apply", "error");
    dispatch(applyLoyalty({ amount: amt, dollarPerPoint: loyaltyCfg.dollarPerPoint }));
    afterTenderAdded(0);
  };
  const onStoreCredit = () => {
    // Android validStoreCredit guard chain (cart / customer / balance / split / already).
    const v = validStoreCredit(store.getState().webRegisterCart, { hasCustomer: !!cart.customer, available: scBalance });
    if (!v.ok) return ToastifyAlert(v.message, "error");
    const amt = Math.min(pay, scBalance);
    if (amt <= 0) return ToastifyAlert("Nothing to apply", "error");
    dispatch(applyStoreCredit({ amount: amt }));
    afterTenderAdded(0);
  };

  // Android "Payments Applied" Refund (ATP:6669): an approved card leg is refunded on the terminal
  // (base + NCA + tip + cash-back) BEFORE it leaves the order — never just dropped from state.
  const [refundIdx, setRefundIdx] = useState(null);
  const removeTender = async (i) => {
    const p = (store.getState().webRegisterCart.payments || [])[i];
    if (!p) return;
    const isCard = String(p.payType).toLowerCase() !== "cash" && !!p.paymentId;
    if (!isCard) {
      dispatch(removePayment(i));
      return;
    }
    if (!canRegister("MENU_CANCEL_REFUND_PAYMENT")) return ToastifyAlert("Access Denied!", "error");
    if (refundIdx !== i) {
      setRefundIdx(i);
      return;
    }
    setRefundIdx(null);
    setBusy(true);
    try {
      // Android ATP:6669-6673 — the leg's base + NCA + tip (cash back was handed out in cash).
      const amount = roundHalfUp((Number(p.amount) || 0) + (Number(p.ncaAmount) || 0) + (Number(p.tip) || 0));
      const gw = await nodeRefundTerminalPayment({ paymentId: p.paymentId, amount, description: `Split refund ${orderIdRef.current}` });
      if (!gw || gw.status !== true) {
        ToastifyAlert(`Card refund failed (${(gw && (gw.reason || gw.message)) || "gateway error"}) — the payment was kept.`, "error");
        return;
      }
      dispatch(removePayment(i));
      saveActiveOrder(orderIdRef.current, store.getState().webRegisterCart);
      ToastifyAlert(`Refunded ${fmt(amount)} to the card`, "success");
    } catch (e) {
      ToastifyAlert("Card refund failed — the payment was kept.", "error");
    } finally {
      setBusy(false);
    }
  };

  // Tile prices (Android bindTenderAmounts): Cash/Debit/EBT = cash price; Credit = card price
  // (dual: dualCardCharge; NCA mode: + surcharge); Debit adds its own surcharge in NCA mode only.
  const ncaSettings = cart.ncaRemoved ? null : settings;
  const creditShown = dualOn ? creditQuote().charge : roundHalfUp(pay + ncaFor(ncaSettings, pay, "Credit").amount);
  const debitShown = dualOn ? pay : roundHalfUp(pay + ncaFor(ncaSettings, pay, "Debit").amount);
  const ncaModeOn = !dualOn && settings && settings.isSurcharge === "1";
  const cashbackLimitLeft =
    settings && Number(settings.cashbackLimitAmount) > 0
      ? Math.max(roundHalfUp(Number(settings.cashbackLimitAmount) - (cart.payments || []).reduce((a, p) => a + (Number(p.cashbackAmount) || 0), 0)), 0)
      : null;
  const dualSavings =
    dualOn && (cart.payments || []).length === 0
      ? roundHalfUp(cart.totals.dual.savings)
      : dualOn
      ? roundHalfUp(balanceDue * splitCardBaseRatio(cart.dualPrice, cart.totals.dual) - balanceDue)
      : 0;

  const s = styles(t);
  // Android customerOwes < 0: a lottery / scratcher payout larger than the sale — the register pays
  // the customer in CASH for |customerOwes|; every other tender is "disabled for this transaction"
  // and the amount can't be edited. Until that cash tender is recorded the order is NOT covered.
  const payoutDue = Number(cart.totals.customerOwes) < 0 && (cart.payments || []).length === 0 ? -Number(cart.totals.customerOwes) : 0;
  const covered = balanceDue <= 0 && payoutDue === 0;
  const onCashPayout = withDeziSettled(() => {
    if (!canRegister("MENU_CASH_PAYMENT")) return ToastifyAlert("Access denied", "error");
    dispatch(addPayment({ payType: "cash", amount: -payoutDue }));
    kickDrawer(settings); // cash goes OUT of the drawer
    afterTenderAdded(0);
  });

  return (
    <div className="qv-dlg-overlay" style={s.overlay} onClick={busy ? undefined : handleClose}>
      <div className="qv-dlg-card" style={s.card} onClick={(e) => e.stopPropagation()}>
        <div style={s.head}>
          <span>{view === "giftcard" ? "Gift card" : "Balance"}</span>
          <button style={s.x} onClick={handleClose} disabled={busy}>✕</button>
        </div>

        {/* Editable balance → type a smaller amount to pay part of the balance (split) */}
        <div style={s.balanceWrap}>
          {covered ? (
            <div style={s.balanceBox}>{fmt(0)}</div>
          ) : (
            <NumericInput value={payAmount} onChange={setPayAmount} style={s.balanceBox} /> /* uncapped: Android cashPayment takes a tender above the due, excess = change */
          )}
          {!covered && <div style={s.adjustHint}>Type an amount to make a partial payment</div>}
          {(cart.payments.length > 0 || cart.giftCards.length > 0 || cart.loyaltyAmount > 0 || cart.storeCreditAmount > 0) && (
            <div style={s.balDueRow}><span style={s.muted}>Balance due</span><span style={s.balDueVal}>{fmt(balanceDue)}</span></div>
          )}
          {/* Android ATP:2165-2270 summary panel — card columns in dual price; Remaining = the Pay amount rule. */}
          {cart.totals.display && (
            <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 6, display: "grid", gridTemplateColumns: "1fr auto", rowGap: 2 }}>
              <span>Subtotal</span><span>{fmt(cart.totals.display.subtotal)}</span>
              {cart.totals.display.orderDiscount > 0 && (<><span>Discount</span><span>−{fmt(cart.totals.display.orderDiscount)}</span></>)}
              <span>Tax</span><span>{fmt((cart.totals.display.taxLines || []).reduce((a, x) => a + (Number(x.amount) || 0), 0))}</span>
              {(cart.payments || []).some((p) => Number(p.tip) > 0) && (
                <><span>Tip</span><span>{fmt((cart.payments || []).reduce((a, p) => a + (Number(p.tip) || 0), 0))}</span></>
              )}
              <span style={{ fontWeight: 600 }}>Total</span><span style={{ fontWeight: 600 }}>{fmt(cart.totals.display.total)}</span>
              {(cart.payments || []).length > 0 && (
                <>
                  <span>Total Paid</span>
                  <span>{fmt((cart.payments || []).reduce((a, p) => a + (Number(p.amount) || 0) + (Number(p.ncaAmount) || 0) + (Number(p.tip) || 0) + (Number(p.cashbackAmount) || 0) + (Number(p.cashbackFee) || 0), 0))}</span>
                  <span style={{ fontWeight: 600 }}>Remaining Balance</span><span style={{ fontWeight: 600 }}>{fmt(cart.totals.display.payAmount)}</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* applied reductions + tenders (removable) */}
        {(cart.giftCards.length > 0 || cart.loyaltyAmount > 0 || cart.storeCreditAmount > 0 || cart.payments.length > 0) && (
          <div style={s.applied}>
            {cart.giftCards.map((g) => (
              <Chip key={g.number} t={t} label={`Gift ••${String(g.number).slice(-4)}`} amount={`−${fmt(g.amountUsed)}`} onRemove={() => dispatch(removeGiftCard(g.number))} />
            ))}
            {cart.loyaltyAmount > 0 && <Chip t={t} label={`Loyalty ${cart.loyaltyPointsSpent}pt`} amount={`−${fmt(cart.loyaltyAmount)}`} onRemove={() => dispatch(clearLoyalty())} />}
            {cart.storeCreditAmount > 0 && <Chip t={t} label="Store credit" amount={`−${fmt(cart.storeCreditAmount)}`} onRemove={() => dispatch(clearStoreCredit())} />}
            {cart.payments.map((p, i) => (
              <Chip
                key={i}
                t={t}
                label={refundIdx === i ? "Tap ✕ again to refund" : payLabel(p)}
                amount={
                  dualOn || !(Number(p.ncaAmount) > 0)
                    ? fmt(roundHalfUp((Number(p.amount) || 0) + (dualOn ? Number(p.ncaAmount) || 0 : 0)))
                    : `${fmt(p.amount)} + NCA ${fmt(p.ncaAmount)}`
                }
                onRemove={() => removeTender(i)}
              />
            ))}
          </div>
        )}

        {saveError && <div style={s.err}>{saveError}<button style={s.retry} onClick={() => finalize(0)} disabled={busy}>Retry save</button></div>}

        {view === "select" && (
          <>
            {covered ? (
              <div style={s.pad}>
                <button style={s.complete} onClick={() => finalize(0)} disabled={busy}>{busy ? "Saving…" : "Complete order"}</button>
              </div>
            ) : payoutDue > 0 ? (
              <div style={s.pad}>
                <div style={{ fontWeight: 600, marginBottom: 10 }}>Payout exceeds the sale — pay the customer {fmt(payoutDue)} in cash.</div>
                <button style={s.complete} onClick={onCashPayout} disabled={busy}>{busy ? "Saving…" : `Cash · pay out ${fmt(payoutDue)}`}</button>
                <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 8 }}>Credit, debit, EBT, loyalty, store credit and gift card are disabled for this transaction.</div>
              </div>
            ) : (
              <>
                {dualSavings > 0 && payoutDue === 0 && (
                  <div style={{ margin: "0 16px 8px", padding: "8px 12px", borderRadius: 10, background: "var(--accent-soft)", fontWeight: 600 }}>
                    💰 Save {fmt(dualSavings)} with Cash or Debit!
                  </div>
                )}
                {ncaModeOn && (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 16px 8px", fontWeight: 600 }}>
                    <input type="checkbox" checked={!cart.ncaRemoved} onChange={(e) => dispatch(setNcaRemoved(!e.target.checked))} />
                    Non-Cash Adjustment
                  </label>
                )}
                <div style={s.grid}>
                  <Tile t={t} label="Loyalty" color="#CA8A04" disabled={!loyaltyAvailable} hint={loyaltyAvailable ? `$${loyaltyBal.dollars.toFixed(2)}` : cart.customer ? "N/A" : "Add customer"} onClick={onLoyalty} />
                  <Tile t={t} label="Store Credit" color="var(--ink-2)" disabled={!scAvailable} hint={scAvailable ? `$${scBalance.toFixed(2)}` : cart.customer ? "N/A" : "Add customer"} onClick={onStoreCredit} />
                  <Tile t={t} label={`Cash (${fmt(Number(payAmount) > balanceDue ? payAmount : pay)})`} color="#16A34A" onClick={onCash} />
                  <Tile t={t} label={`Credit (${fmt(creditShown)})`} color="var(--accent)" onClick={() => startCard("credit")} />
                  {/* QV-7233 "Disable Debit Card": flag "5" in the store's `ebt` list turns debit OFF
                      (inverted sense vs the other flags in that list, which each ENABLE a tile). */}
                  <Tile t={t} label={`Debit (${fmt(debitShown)})`} color="#7C3AED" disabled={isDebitCardDisabled(settings)} onClick={() => startCard("debit")} />
                  {/* Android settingBaseButtons(): EBT Cash ↔ ebt "2", EBT Food ↔ ebt "1", Gift Card ↔ ebt "3" (disabled otherwise). */}
                  <Tile t={t} label="EBT Cash" color="#0891B2" disabled={!ebtCashEnabled(settings)} onClick={() => startCard("ebtcash")} />
                  <Tile t={t} label="EBT Food" color="#EA580C" disabled={!ebtFoodEnabled(settings)} onClick={() => startCard("ebtfood")} />
                  <Tile t={t} label="Gift Card" color="#DB2777" disabled={!giftCardTenderEnabled(settings)} onClick={() => (canRegister("MENU_GIFT_CARD_ISSUE_REDEEM") ? setView("giftcard") : ToastifyAlert("Access denied", "error"))} />
                </div>
                <div style={s.qcWrap}>
                  <div style={s.muted}>Quick Cash</div>
                  <div style={s.quick}>
                    {quickCashList(balanceDue).map((v, i) => (
                        <button key={i} style={s.qbtn} onClick={() => onQuickCash(Number(v))}>{fmt(v)}</button>
                      ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {view === "giftcard" && (
          <div style={s.panel}>
            <div style={s.fieldLabel}>Gift card number</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input style={{ ...s.input, flex: 1 }} value={gcNumber} onChange={(e) => { setGcNumber(e.target.value); setGcLookup(null); }} placeholder="Scan or enter" autoFocus />
              <button style={s.lookup} onClick={doGiftLookup} disabled={gcBusy}>{gcBusy ? "…" : "Look up"}</button>
            </div>
            {gcLookup && gcLookup.error && <div style={s.errText}>{gcLookup.error}</div>}
            {gcLookup && gcLookup.balance != null && (
              <>
                <div style={s.avail}>Balance: {fmt(gcLookup.balance)}</div>
                <div style={s.fieldLabel}>Amount to redeem</div>
                <NumericInput style={s.input} value={gcAmount} onChange={setGcAmount} max={gcLookup.balance} />
                <button style={s.apply} onClick={onApplyGift}>Apply gift card</button>
              </>
            )}
            <button style={s.back} onClick={() => setView("select")}>Back</button>
          </div>
        )}
      </div>

      {terminal && (
        <TerminalPaymentModal cardType={terminal.cardType} amount={terminal.amount} orderId={terminal.terminalOrderId} idempotencyKey={terminal.idempotencyKey} onApproved={onCardApproved} onBack={() => { setTerminal(null); restoreAmount(); }} />
      )}
      {cashbackPrompt && (
        <div className="qv-dlg-overlay" style={s.overlay} onClick={() => { setCashbackPrompt(null); restoreAmount(); }}>
          <div className="qv-dlg-card" style={s.card} onClick={(e) => e.stopPropagation()}>
            <div style={s.head}><span>Cash back</span><button style={s.x} onClick={() => { setCashbackPrompt(null); restoreAmount(); }}>✕</button></div>
            <div style={{ padding: 18 }}>
              <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 8 }}>
                Enter cash-back amount (0 for none){cashbackLimitLeft != null ? ` · max ${fmt(cashbackLimitLeft)}` : ""}
              </div>
              <NumericInput
                style={{ width: "100%", height: 48, borderRadius: 10, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.inputText, padding: "0 14px", fontSize: 18, fontWeight: 500, boxSizing: "border-box" }}
                value={cashbackPrompt.value}
                // Android :6520 — cash back is capped at the order limit minus cash back already given
                onChange={(v) => setCashbackPrompt((p) => ({ ...p, value: cashbackLimitLeft != null ? Math.min(Number(v) || 0, cashbackLimitLeft) : v }))}
                decimals={2}
                autoFocus
                onEnter={() => { const k = cashbackPrompt.key; const v = Number(cashbackPrompt.value) || 0; setCashbackPrompt(null); doStartCard(k, v); }}
              />
              <button
                style={{ width: "100%", height: 50, marginTop: 12, borderRadius: 12, border: "none", background: t.accent, color: "#fff", fontWeight: 800, fontSize: 16, cursor: "pointer" }}
                onClick={() => { const k = cashbackPrompt.key; const v = Number(cashbackPrompt.value) || 0; setCashbackPrompt(null); doStartCard(k, v); }}
              >Continue to debit</button>
              {/* Android 41cb76b780 — "Amount to charge" = debit price + cash-back charge + cash back */}
              <div style={{ fontSize: 13, color: t.textMuted, marginTop: 10, textAlign: "center" }}>
                Amount to charge: {fmt(roundHalfUp(debitShown + (Number(cashbackPrompt.value) > 0 ? cashbackFeeFor(settings, "Debit") : 0) + (Number(cashbackPrompt.value) || 0)))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function payLabel(p) {
  if (p.payType === "cash") return "Cash";
  if (p.cardType) return p.cardType + (p.last4 ? ` ••${p.last4}` : "");
  return p.payType;
}

function Chip({ t, label, amount, onRemove }) {
  const s = styles(t);
  return (
    <div style={s.chip}>
      <span>{label}</span>
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>{amount}<button style={s.rm} onClick={onRemove}>✕</button></span>
    </div>
  );
}

function Tile({ t, label, color, onClick, disabled, hint }) {
  const s = styles(t);
  return (
    <button style={{ ...s.tile, borderColor: color, opacity: disabled ? 0.5 : 1 }} onClick={disabled ? undefined : onClick} disabled={disabled}>
      <span style={s.tileLabel}>{label}</span>
      {hint && <span style={s.hintTag}>{hint}</span>}
    </button>
  );
}

const styles = (t) => ({
  overlay: { position: "fixed", inset: 0, background: "rgba(2,6,23,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 },
  card: { width: "100%", maxWidth: 480, background: t.card || "#0f1a2f", color: t.text || "#E5EDF7", borderRadius: 16, border: `1px solid ${t.cardBorder || "#1f2c47"}`, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "92vh" },
  head: { padding: "14px 18px", fontWeight: 800, fontSize: 16, borderBottom: `1px solid ${t.divider || "#1f2c47"}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.surfaceAlt || "#0f1a2f" },
  x: { border: "none", background: "none", cursor: "pointer", fontSize: 18, color: t.textMuted || "var(--ink-3)" },
  balanceWrap: { padding: "16px 18px 10px", display: "flex", flexDirection: "column", gap: 4 },
  balanceBox: { width: "100%", textAlign: "right", fontSize: 36, fontWeight: 800, padding: "12px 16px", borderRadius: 12, border: `1px solid ${t.inputBorder || "#25324d"}`, background: t.inputBg || "#0f1b30", color: t.text || "#E5EDF7", outline: "none", boxSizing: "border-box" },
  adjustHint: { textAlign: "right", fontSize: 13, color: t.accent || "#4C8DFF" },
  balDueRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 },
  muted: { color: t.textMuted || "var(--ink-3)", fontSize: 13 },
  balDueVal: { fontSize: 18, fontWeight: 800, color: t.price || "#4C8DFF" },
  applied: { padding: "0 18px 10px", display: "flex", flexDirection: "column", gap: 6, maxHeight: 130, overflow: "auto" },
  chip: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, background: t.surfaceAlt || "#0f1b30", borderRadius: 8, padding: "6px 10px", color: t.textMuted || "var(--ink-3)" },
  rm: { border: "none", background: "none", color: "#EF4444", cursor: "pointer", fontSize: 12 },
  err: { margin: "0 18px 10px", padding: 10, borderRadius: 8, background: "rgba(239,68,68,0.12)", color: "#EF4444", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  retry: { border: "none", background: "#EF4444", color: "#fff", borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontWeight: 500 },
  pad: { padding: 16 },
  grid: { padding: "6px 16px 12px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  tile: { height: 58, borderRadius: 12, border: "2px solid", background: t.surfaceAlt || "#0f1b30", color: t.text || "#E5EDF7", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, fontWeight: 500, fontSize: 15 },
  tileLabel: { fontSize: 15, fontWeight: 500 },
  hintTag: { fontSize: 11, color: t.textMuted || "var(--ink-3)" },
  complete: { width: "100%", height: 54, borderRadius: 12, border: "none", background: "#059669", color: "#fff", fontWeight: 800, fontSize: 17, cursor: "pointer" },
  qcWrap: { padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 6 },
  quick: { display: "flex", gap: 8 },
  qbtn: { flex: 1, height: 40, borderRadius: 8, border: `1px solid ${t.cardBorder || "#25324d"}`, background: t.surfaceAlt || "#0f1b30", color: t.text || "#E5EDF7", cursor: "pointer", fontWeight: 500 },
  panel: { padding: 18, display: "flex", flexDirection: "column", gap: 12 },
  fieldLabel: { fontSize: 13, color: t.textMuted || "var(--ink-3)" },
  input: { width: "100%", height: 48, borderRadius: 10, border: `1px solid ${t.inputBorder || "#25324d"}`, background: t.inputBg || "#0f1b30", color: t.inputText || "#E5EDF7", padding: "0 14px", fontSize: 18, fontWeight: 500, outline: "none", boxSizing: "border-box" },
  avail: { fontSize: 14, fontWeight: 500, color: t.price || "#4C8DFF" },
  errText: { fontSize: 13, color: "#EF4444" },
  lookup: { width: 110, height: 48, borderRadius: 10, border: "none", background: t.accent || "var(--accent)", color: "#fff", fontWeight: 500, cursor: "pointer" },
  apply: { height: 50, borderRadius: 12, border: "none", background: "#059669", color: "#fff", fontWeight: 800, fontSize: 16, cursor: "pointer" },
  back: { height: 44, borderRadius: 10, border: `1px solid ${t.cardBorder || "#25324d"}`, background: "transparent", color: t.text || "#E5EDF7", fontWeight: 500, cursor: "pointer" },
});
